/**
 * Skill Loader
 * Loads Claude Code-style SKILL.md files from:
 * - ~/.metis/skills/ (global)
 * - .metis/skills/ (project)
 *
 * Skills are folders containing:
 * - SKILL.md (required) - YAML frontmatter + instructions
 * - REFERENCE.md (optional) - Additional reference content
 * - scripts/ (optional) - Executable scripts
 * - resources/ (optional) - Other resources
 */

import fs from 'fs';
import path from 'path';
import os from 'os';

export interface SkillMetadata {
  name: string;
  description: string;
  dependencies?: string[];
  version?: string;
  author?: string;
  tags?: string[];
}

export interface LoadedSkill {
  /** Skill directory path */
  path: string;

  /** Skill metadata from frontmatter */
  metadata: SkillMetadata;

  /** Full content of SKILL.md (without frontmatter) */
  instructions: string;

  /** Reference content if REFERENCE.md exists */
  reference?: string;

  /** Available scripts */
  scripts: string[];

  /** Available resources */
  resources: string[];

  /** Source level (global or project) */
  level: 'global' | 'project';

  /** Last modified time */
  lastModified: Date;

  /** Whether skill is currently active */
  active: boolean;
}

export interface SkillContext {
  /** User's prompt/task */
  prompt: string;

  /** Current working directory */
  cwd: string;

  /** Files mentioned in prompt */
  mentionedFiles?: string[];

  /** Detected programming languages */
  languages?: string[];

  /** Task type (e.g., 'write', 'debug', 'review') */
  taskType?: string;
}

export class SkillLoader {
  private projectRoot: string;
  private homeDir: string;
  private loadedSkills: Map<string, LoadedSkill> = new Map();
  private cache: { skills: LoadedSkill[]; timestamp: number } | null = null;
  private cacheTTL = 30000; // 30 seconds

  constructor(projectRoot: string) {
    this.projectRoot = projectRoot;
    this.homeDir = os.homedir();
  }

  /**
   * Load all skills from global and project directories
   */
  loadSkills(forceRefresh: boolean = false): LoadedSkill[] {
    const now = Date.now();

    if (!forceRefresh && this.cache && (now - this.cache.timestamp) < this.cacheTTL) {
      return this.cache.skills;
    }

    const skills: LoadedSkill[] = [];

    // Load global skills
    const globalSkillsDir = path.join(this.homeDir, '.metis', 'skills');
    skills.push(...this.loadSkillsFromDirectory(globalSkillsDir, 'global'));

    // Load project skills (can override global)
    const projectSkillsDir = path.join(this.projectRoot, '.metis', 'skills');
    skills.push(...this.loadSkillsFromDirectory(projectSkillsDir, 'project'));

    // Update cache
    this.cache = { skills, timestamp: now };

    // Update loaded skills map
    this.loadedSkills.clear();
    for (const skill of skills) {
      this.loadedSkills.set(skill.metadata.name, skill);
    }

    return skills;
  }

  /**
   * Load skills from a specific directory
   */
  private loadSkillsFromDirectory(
    skillsDir: string,
    level: 'global' | 'project'
  ): LoadedSkill[] {
    const skills: LoadedSkill[] = [];

    if (!fs.existsSync(skillsDir)) {
      return skills;
    }

    try {
      const entries = fs.readdirSync(skillsDir, { withFileTypes: true });

      for (const entry of entries) {
        if (entry.isDirectory()) {
          const skillPath = path.join(skillsDir, entry.name);
          const skill = this.loadSkill(skillPath, level);
          if (skill) {
            skills.push(skill);
          }
        }
      }
    } catch (error) {
      console.warn(`Failed to load skills from ${skillsDir}:`, error);
    }

    return skills;
  }

  /**
   * Load a single skill from directory
   */
  private loadSkill(skillDir: string, level: 'global' | 'project'): LoadedSkill | null {
    const skillMdPath = path.join(skillDir, 'SKILL.md');

    if (!fs.existsSync(skillMdPath)) {
      return null;
    }

    try {
      const content = fs.readFileSync(skillMdPath, 'utf8');
      const { metadata, body } = this.parseFrontmatter(content);

      if (!metadata.name || !metadata.description) {
        console.warn(`Skill at ${skillDir} missing required name or description`);
        return null;
      }

      // Load reference content if exists
      const referencePath = path.join(skillDir, 'REFERENCE.md');
      const reference = fs.existsSync(referencePath)
        ? fs.readFileSync(referencePath, 'utf8')
        : undefined;

      // Find available scripts
      const scriptsDir = path.join(skillDir, 'scripts');
      const scripts = fs.existsSync(scriptsDir)
        ? fs.readdirSync(scriptsDir).filter(f => f.endsWith('.py') || f.endsWith('.js') || f.endsWith('.sh'))
        : [];

      // Find available resources
      const resourcesDir = path.join(skillDir, 'resources');
      const resources = fs.existsSync(resourcesDir)
        ? fs.readdirSync(resourcesDir)
        : [];

      const stats = fs.statSync(skillMdPath);

      return {
        path: skillDir,
        metadata,
        instructions: body.trim(),
        reference,
        scripts,
        resources,
        level,
        lastModified: stats.mtime,
        active: false,
      };
    } catch (error) {
      console.warn(`Failed to load skill from ${skillDir}:`, error);
      return null;
    }
  }

  /**
   * Parse YAML frontmatter from SKILL.md content
   */
  private parseFrontmatter(content: string): { metadata: SkillMetadata; body: string } {
    const defaultMetadata: SkillMetadata = {
      name: '',
      description: '',
    };

    // Check for frontmatter
    if (!content.startsWith('---')) {
      return { metadata: defaultMetadata, body: content };
    }

    const endMatch = content.indexOf('---', 3);
    if (endMatch === -1) {
      return { metadata: defaultMetadata, body: content };
    }

    const frontmatter = content.substring(3, endMatch).trim();
    const body = content.substring(endMatch + 3).trim();

    // Parse simple YAML (name: value format)
    const metadata: Record<string, any> = {};
    const lines = frontmatter.split('\n');

    for (const line of lines) {
      const colonIndex = line.indexOf(':');
      if (colonIndex !== -1) {
        const key = line.substring(0, colonIndex).trim();
        let value: any = line.substring(colonIndex + 1).trim();

        // Handle arrays (simple format: key: [item1, item2])
        if (value.startsWith('[') && value.endsWith(']')) {
          value = value.slice(1, -1).split(',').map((s: string) => s.trim());
        }

        metadata[key] = value;
      }
    }

    return {
      metadata: {
        name: metadata.name || '',
        description: metadata.description || '',
        dependencies: metadata.dependencies,
        version: metadata.version,
        author: metadata.author,
        tags: metadata.tags,
      },
      body,
    };
  }

  /**
   * Find relevant skills for a given context
   */
  findRelevantSkills(context: SkillContext): LoadedSkill[] {
    const skills = this.loadSkills();
    const relevant: LoadedSkill[] = [];
    const promptLower = context.prompt.toLowerCase();

    for (const skill of skills) {
      const score = this.scoreSkillRelevance(skill, context, promptLower);
      if (score > 0) {
        relevant.push({ ...skill, active: true });
      }
    }

    // Sort by relevance (most relevant first)
    return relevant.sort((a, b) => {
      const scoreA = this.scoreSkillRelevance(a, context, promptLower);
      const scoreB = this.scoreSkillRelevance(b, context, promptLower);
      return scoreB - scoreA;
    });
  }

  /**
   * Score how relevant a skill is to the context
   */
  private scoreSkillRelevance(
    skill: LoadedSkill,
    context: SkillContext,
    promptLower: string
  ): number {
    let score = 0;

    // Check if skill name appears in prompt
    if (promptLower.includes(skill.metadata.name.toLowerCase())) {
      score += 10;
    }

    // Check tags
    if (skill.metadata.tags) {
      for (const tag of skill.metadata.tags) {
        if (promptLower.includes(tag.toLowerCase())) {
          score += 5;
        }
      }
    }

    // Check description keywords
    const descWords = skill.metadata.description.toLowerCase().split(/\s+/);
    for (const word of descWords) {
      if (word.length > 3 && promptLower.includes(word)) {
        score += 2;
      }
    }

    // Check against detected languages
    if (context.languages && skill.metadata.tags) {
      for (const lang of context.languages) {
        if (skill.metadata.tags.includes(lang.toLowerCase())) {
          score += 8;
        }
      }
    }

    // Check task type
    if (context.taskType) {
      const taskTypeLower = context.taskType.toLowerCase();
      if (skill.metadata.description.toLowerCase().includes(taskTypeLower)) {
        score += 6;
      }
    }

    return score;
  }

  /**
   * Get a skill by name
   */
  getSkill(name: string): LoadedSkill | undefined {
    this.loadSkills();
    return this.loadedSkills.get(name);
  }

  /**
   * Get all loaded skills
   */
  getAllSkills(): LoadedSkill[] {
    return this.loadSkills();
  }

  /**
   * Generate context for system prompt including active skills
   */
  generateSkillsContext(activeSkills: LoadedSkill[]): string {
    if (activeSkills.length === 0) {
      return '';
    }

    const sections: string[] = [
      '<active-skills>',
      'The following skills are active for this task:',
      '',
    ];

    for (const skill of activeSkills) {
      sections.push(`## ${skill.metadata.name}`);
      sections.push(`Description: ${skill.metadata.description}`);
      sections.push('');
      sections.push(skill.instructions);
      sections.push('');

      if (skill.reference) {
        sections.push('### Reference');
        sections.push(skill.reference);
        sections.push('');
      }
    }

    sections.push('</active-skills>');

    return sections.join('\n');
  }

  /**
   * Create a new skill
   */
  createSkill(
    name: string,
    description: string,
    instructions: string,
    global: boolean = false
  ): string {
    const skillsDir = global
      ? path.join(this.homeDir, '.metis', 'skills')
      : path.join(this.projectRoot, '.metis', 'skills');

    const skillDir = path.join(skillsDir, name);

    // Create directories
    if (!fs.existsSync(skillDir)) {
      fs.mkdirSync(skillDir, { recursive: true });
    }

    // Create SKILL.md
    const content = `---
name: ${name}
description: ${description}
tags: []
---

# ${name}

${instructions}
`;

    fs.writeFileSync(path.join(skillDir, 'SKILL.md'), content);

    // Clear cache
    this.cache = null;

    return skillDir;
  }

  /**
   * Delete a skill
   */
  deleteSkill(name: string, global: boolean = false): boolean {
    const skillsDir = global
      ? path.join(this.homeDir, '.metis', 'skills')
      : path.join(this.projectRoot, '.metis', 'skills');

    const skillDir = path.join(skillsDir, name);

    if (fs.existsSync(skillDir)) {
      fs.rmSync(skillDir, { recursive: true });
      this.cache = null;
      return true;
    }

    return false;
  }

  /**
   * Clear cache
   */
  clearCache(): void {
    this.cache = null;
  }
}

// Global skill loader instance
let globalSkillLoader: SkillLoader | null = null;

export function getSkillLoader(projectRoot?: string): SkillLoader {
  if (!globalSkillLoader) {
    globalSkillLoader = new SkillLoader(projectRoot || process.cwd());
  }
  return globalSkillLoader;
}

export function resetSkillLoader(): void {
  globalSkillLoader = null;
}
